/**
 * Option lists shared by every Call Report drawer.
 *
 * One fetch of `/reports/calls/filters` serves all four reports: timezones,
 * queues, campaigns, agents, IVR menus, dispositions and trunks, plus the
 * interval and direction whitelists the API actually enforces.
 *
 * Served rather than hardcoded so the list a drawer offers and the list the
 * query accepts cannot drift apart — the same reasoning as the conversation
 * reports' org-filters endpoint.
 */
import { useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface FilterOption {
  label: string
  value: string
}
export interface TimezoneOption extends FilterOption {
  offset: string
}

export interface CallFilterOptions {
  timezones: TimezoneOption[]
  queues: FilterOption[]
  campaigns: FilterOption[]
  agents: FilterOption[]
  ivrMenus: FilterOption[]
  dispositions: FilterOption[]
  trunks: FilterOption[]
  intervalOptions: FilterOption[]
  directionOptions: FilterOption[]
}

const EMPTY: CallFilterOptions = {
  timezones: [],
  queues: [],
  campaigns: [],
  agents: [],
  ivrMenus: [],
  dispositions: [],
  trunks: [],
  intervalOptions: [],
  directionOptions: [],
}

export function useCallReportFilters(): { options: CallFilterOptions; loading: boolean } {
  const [options, setOptions] = useState<CallFilterOptions>(EMPTY)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const res = await api.get<CallFilterOptions>('/reports/calls/filters')
        if (!cancelled) setOptions({ ...EMPTY, ...res })
      } catch {
        // A failed catalog leaves the drawer usable: the window fields are
        // what the report actually requires, and the id filters are optional.
        if (!cancelled) setOptions(EMPTY)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { options, loading }
}

/**
 * The intervals to offer when the catalog has not loaded (or failed).
 *
 * A drawer whose only required non-date field is empty cannot be submitted,
 * so the fallback is the same whitelist the API enforces rather than nothing.
 */
export const FALLBACK_INTERVALS: FilterOption[] = [
  { value: '15m', label: '15 Minutes' },
  { value: '30m', label: '30 Minutes' },
  { value: '60m', label: '60 Minutes Hourly' },
  { value: '1d', label: '24 Hours Day Wise' },
]
